import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  CHECKOUT_ROOT_STYLE,
} from '@/lib/checkout-theme';
import imobdeskLogo from '@/assets/imobdesk-logo.svg';
import { cn } from '@/lib/utils';

type ContratacaoPublicShellProps = {
  children: ReactNode;
  /** Classes extras no card branco (`cx-left-inner`). */
  innerClassName?: string;
};

/**
 * Mesmo shell visual do fluxo `/contratar`: fundo escuro, orbes, topo com logo e card flutuante.
 */
export function ContratacaoPublicShell({ children, innerClassName }: ContratacaoPublicShellProps) {
  const reducedMotion = useReducedMotion();

  return (
    <div className="cx cx--dark" style={CHECKOUT_ROOT_STYLE}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        {!reducedMotion ? (
          <>
            <motion.div
              className="absolute -top-24 right-[8%] h-80 w-80 rounded-full bg-indigo-500/25 blur-[92px]"
              initial={{ opacity: 0.32 }}
              animate={{ opacity: [0.28, 0.44, 0.28], scale: [1, 1.06, 1] }}
              transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute -bottom-32 -left-28 h-96 w-96 rounded-full bg-violet-500/20 blur-[100px]"
              initial={{ opacity: 0.24 }}
              animate={{ opacity: [0.2, 0.36, 0.2], scale: [1, 1.05, 1] }}
              transition={{ duration: 12.5, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
            />
          </>
        ) : (
          <>
            <div className="absolute -top-24 right-[8%] h-80 w-80 rounded-full bg-indigo-500/18 blur-[92px]" />
            <div className="absolute -bottom-32 -left-28 h-96 w-96 rounded-full bg-violet-500/14 blur-[100px]" />
          </>
        )}
      </div>

      <div className="cx-top">
        <img src={imobdeskLogo} alt="ImobDesk" className="cx-logo cx-logo--invert" />
      </div>

      <div className="cx-main cx-main--cardfloat">
        <div className="cx-left">
          <motion.div
            className={cn('cx-left-inner', innerClassName)}
            initial={
              reducedMotion ? { opacity: 0 } : { opacity: 0, y: 18, filter: 'blur(18px)', scale: 0.985 }
            }
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
            transition={{ duration: reducedMotion ? 0.16 : 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
